import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Slide,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Switch,
  Link,
  Chip,
} from '@mui/material';
import {
  PlayArrow as ArrowBackIcon,
  Visibility as PreviewIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// Types
import { SiteEditState } from '../../types/visualEditor';

// Services & Hooks
import { ImageService, ImageMetadata } from '../../services/imageService';
import { useSites } from '../../hooks/useSites';
import { useImageUpload } from '../../hooks/useImageUpload';
import { useSnackbar } from '../../hooks/useSnackbar';
import { DomainService } from '../../services/domainService';
import { SiteService } from '../../services/siteService';
import { getDisplayUrl, getPreviewUrl } from '../../utils/urlHelper';
import { getStatusColor, getStatusLabel, isStatusPublished } from '../../utils/statusHelper';

// Main Components
import { PageConfigurationTab } from './PageConfigurationTab';
import { ImageLibraryTab } from './ImageLibraryTab';

// Visual Editor Components
import { TabPanel } from './visualEditor/TabPanel';
import {
  SiteSettingsTab,
  PopupCTATab,
  SEOTagsTab,
  CompanyInfoTab,
} from './visualEditor/tabs';
import { ConfirmDialog, SaveButton } from './common';

const SESSION_KEY_CREATED_EDIT_STATE = 'visualEditor_createdEditState';

export const VisualEditor: React.FC = () => {
  const { siteId: paramSiteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { sites, updateSite, createSite, publishSite, unpublishSite } = useSites();
  const { uploadImage } = useImageUpload();
  const pendingPageImageFilesRef = useRef<Map<string, File>>(new Map());
  const pendingBackgroundImageFileRef = useRef<File | null>(null);
  const pendingCtaImageFilesRef = useRef<Map<string, File>>(new Map());
  const pendingCtaHoverImageFilesRef = useRef<Map<string, File>>(new Map());
  const pendingLogoFileRef = useRef<File | null>(null);
  const pendingFaviconFileRef = useRef<File | null>(null);
  const pendingSlideImageFilesRef = useRef<Map<string, File>>(new Map());
  /** 新規作成保存直後: navigate 前に入れ、遷移後の useEffect で editState を復元する（同一インスタンスで ref が残る） */
  const pendingCreatedStateRef = useRef<{ siteId: string; editState: SiteEditState } | null>(null);
  /** 上記で復元した直後は、続く「sites から読む」で上書きしないようスキップする */
  const skipLoadFromSitesForIdRef = useRef<string | null>(null);
  /** 新規作成の保存処理中は useEffect で editState を空にリセットしない（createSite → setSites で useEffect が走るため） */
  const isSavingFromCreateRef = useRef(false);
  /** どの siteId 用に editState を初期化したか。別サイトに遷移したら sites から再読み込みするため */
  const lastInitializedSiteIdRef = useRef<string | null>(null);
  
  // URLから直接siteIdを取得するフォールバック処理
  const getSiteIdFromUrl = () => {
    const pathname = window.location.pathname;
    const segments = pathname.split('/');
    const sitesIndex = segments.indexOf('sites');
    if (sitesIndex !== -1 && segments[sitesIndex + 1]) {
      return segments[sitesIndex + 1];
    }
    return null;
  };
  
  const siteId = paramSiteId || getSiteIdFromUrl();
  
  const isCreating = siteId === 'create' || !siteId;
  
  // create → edit 遷移時に初回レンダーから画像付き状態を出すため、location.state の initialEditState を初期値に使う
  const navState = location.state as { newSiteId?: string; initialEditState?: SiteEditState } | null;
  const initialEditStateFromNav: SiteEditState | null =
    navState?.initialEditState && !isCreating ? navState.initialEditState : null;
  
  // 統一された状態管理
  const [editState, setEditState] = useState<SiteEditState>(
    initialEditStateFromNav ?? {
      title: '',
      description: '',
      status: 'draft',
      pages: [],
      pageSlots: [],
      maxRows: 1,
      maxColumns: 1,
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      customDomain: '',
      sslEnabled: false,
      settings: undefined,
      companyUserId: undefined,
    }
  );
  
  const [activeMenu, setActiveMenu] = useState('site'); // デフォルトは「サイト」
  const [saving, setSaving] = useState(false);
  const [editInitialized, setEditInitialized] = useState<boolean>(!!initialEditStateFromNav);
  const [modalSaving, setModalSaving] = useState(false); // モーダル内の保存ボタン専用
  const [imageLibraryCache, setImageLibraryCache] = useState<ImageMetadata[]>([]);
  const [imageLibraryLoaded, setImageLibraryLoaded] = useState(false);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [domainConfig, setDomainConfig] = useState<{ isActive: boolean; basePath?: string } | null>(null);
  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useSnackbar();

  // 保存確認モーダル用の状態
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  
  // 保存成功メッセージ用の状態
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);

  // 状態の初期化
  useEffect(() => {
    // create から遷移した直後（nav state で初期表示済み）: この siteId 用に初期化済みとマークしてスキップ
    if (editInitialized && siteId && navState?.newSiteId === siteId && navState?.initialEditState) {
      lastInitializedSiteIdRef.current = siteId;
      return;
    }
    // すでにこの siteId 用に初期化済みなら何もしない（別サイトに切り替えた場合は lastInitializedSiteIdRef !== siteId で再読み込みする）
    if (editInitialized && siteId && lastInitializedSiteIdRef.current === siteId) {
      return;
    }
    
    // 新規作成の場合（保存処理中はリセットしない＝createSite → setSites でこの effect が走っても画像を消さない）
    if (isCreating) {
      if (isSavingFromCreateRef.current) {
        return;
      }
      setEditState({
        title: '',
        description: '',
        status: 'draft',
        pages: [],
        pageSlots: [],
        maxRows: 1,
        maxColumns: 1,
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
        customDomain: '',
        sslEnabled: false,
        settings: undefined,
        companyUserId: undefined,
      });
      setEditInitialized(true);
      lastInitializedSiteIdRef.current = siteId ?? 'create';
      return;
    }
    
    // 新規作成保存直後の遷移: sessionStorage から復元（別ルートでアンマウントされるため ref/state は届かない）
    if (siteId) {
      try {
        const stored = sessionStorage.getItem(`${SESSION_KEY_CREATED_EDIT_STATE}_${siteId}`);
        if (stored) {
          const parsed = JSON.parse(stored) as SiteEditState;
          sessionStorage.removeItem(`${SESSION_KEY_CREATED_EDIT_STATE}_${siteId}`);
          setEditState(parsed);
          setEditInitialized(true);
          lastInitializedSiteIdRef.current = siteId;
          skipLoadFromSitesForIdRef.current = siteId;
          return;
        }
      } catch (_) {
        sessionStorage.removeItem(`${SESSION_KEY_CREATED_EDIT_STATE}_${siteId}`);
      }
    }
    // 同一インスタンスの場合の復元（ref）
    const pending = pendingCreatedStateRef.current;
    if (siteId && pending?.siteId === siteId) {
      setEditState(pending.editState);
      setEditInitialized(true);
      lastInitializedSiteIdRef.current = siteId;
      pendingCreatedStateRef.current = null;
      skipLoadFromSitesForIdRef.current = siteId;
      return;
    }
    // フォールバック: location.state から復元
    const navStateInEffect = location.state as { newSiteId?: string; initialEditState?: SiteEditState } | null;
    if (siteId && navStateInEffect?.newSiteId === siteId && navStateInEffect?.initialEditState) {
      setEditState(navStateInEffect.initialEditState);
      setEditInitialized(true);
      lastInitializedSiteIdRef.current = siteId;
      skipLoadFromSitesForIdRef.current = siteId;
      return;
    }
    // 既存サイトの読み込み（sitesが読み込まれた後にのみ実行）
    if (siteId && sites.length > 0) {
      if (skipLoadFromSitesForIdRef.current === siteId) {
        skipLoadFromSitesForIdRef.current = null;
        return;
      }
      const existingSite = sites.find(site => site.id === siteId);
      
      if (existingSite) {
        setEditState({
          title: existingSite.title,
          description: existingSite.description || '',
          status: existingSite.status,
          pages: existingSite.pages || [],
          pageSlots: [],
          maxRows: existingSite.maxRows || 1,
          maxColumns: existingSite.maxColumns || 1,
          metaTitle: existingSite.settings?.metaTitle || '',
          metaDescription: existingSite.settings?.metaDescription || '',
          metaKeywords: existingSite.settings?.metaKeywords || '',
          customDomain: existingSite.customDomain || '',
          sslEnabled: existingSite.sslEnabled || false,
          settings: existingSite.settings,
          companyUserId: existingSite.companyUserId,
        });
        setEditInitialized(true);
        lastInitializedSiteIdRef.current = siteId;
      } else {
        setEditState({
          title: '',
          description: '',
          status: 'draft',
          pages: [],
          pageSlots: [],
          maxRows: 1,
          maxColumns: 1,
          metaTitle: '',
          metaDescription: '',
          metaKeywords: '',
          customDomain: '',
          sslEnabled: false,
          settings: undefined,
          companyUserId: undefined,
        });
        setEditInitialized(true);
        lastInitializedSiteIdRef.current = siteId;
      }
    }
  }, [siteId, isCreating, sites, location.state]);

  // 画像ライブラリの事前読み込み
  useEffect(() => {
    if (siteId && !imageLibraryLoaded) {
      const loadImageLibrary = async () => {
        try {
          const imageList = await ImageService.getSiteImageLibrary(siteId);
          setImageLibraryCache(imageList);
          setImageLibraryLoaded(true);
        } catch (error) {
          console.error('画像ライブラリの読み込みに失敗しました:', error);
        }
      };
      
      loadImageLibrary();
    }
  }, [siteId, imageLibraryLoaded]);

  // サイトURLの取得とドメイン設定
  useEffect(() => {
    if (siteId && siteId !== 'create' && sites.length > 0) {
      const loadSiteUrl = async () => {
        try {
          const site = sites.find(s => s.id === siteId);
          if (site) {
            const domainConfigData = await DomainService.getDomain(siteId);
            if (domainConfigData && domainConfigData.isActive && domainConfigData.domain) {
              const basePath = domainConfigData.basePath || '';
              const url = getDisplayUrl(domainConfigData.domain, basePath);
              setSiteUrl(url);
              setDomainConfig({ isActive: domainConfigData.isActive, basePath: domainConfigData.basePath });
            } else {
              setSiteUrl(null);
              setDomainConfig(null);
            }
          }
        } catch (error) {
          console.error('サイトURLの取得に失敗しました:', error);
          setSiteUrl(null);
          setDomainConfig(null);
        }
      };
      
      loadSiteUrl();
    } else {
      setSiteUrl(null);
      setDomainConfig(null);
    }
  }, [siteId, sites]);

  // ローカルストレージの自動保存は削除（Firebaseのデータのみ使用）

  const handleMenuClick = (menuId: string) => {
    setActiveMenu(menuId);
  };

  const handleBack = () => {
    navigate('/admin/sites');
  };

  const handlePreview = () => {
    if (siteId && siteId !== 'create') {
      window.open(getPreviewUrl(siteId), '_blank');
    }
  };

  // 状態更新関数（useCallbackでメモ化）
  const updateEditState = useCallback((updates: Partial<SiteEditState>) => {
    setEditState(prev => ({ ...prev, ...updates }));
  }, []);

  // 有効なカラーコードかどうかをチェックする関数
  const isValidColorCode = (color: string | undefined): boolean => {
    if (!color) return true; // undefinedや空文字は有効（設定されていない場合は問題なし）
    // #rrggbb形式（6桁の16進数）またはrgba()形式をチェック
    return /^#[0-9A-Fa-f]{6}$/.test(color) || /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color);
  };

  const handleSaveClick = () => {
    setSaveConfirmOpen(true);
  };

  const handleSaveConfirm = async () => {
    setModalSaving(true);
    try {
      await handleSave(true, true); // suppressNotification=true, suppressSavingState=true
      setSaveConfirmOpen(false);
      setSaveSuccessOpen(true);
      setTimeout(() => {
        setSaveSuccessOpen(false);
      }, 2000);
    } catch (error) {
      // エラーはhandleSave内で処理される
    } finally {
      setModalSaving(false);
    }
  };

  const handleSaveCancel = () => {
    setSaveConfirmOpen(false);
  };

  const handleSave = async (suppressNotification?: boolean, suppressSavingState?: boolean) => {
    const shouldSuppress = suppressNotification === true;
    const shouldSuppressSavingState = suppressSavingState === true;
    try {
      if (!shouldSuppressSavingState) {
      setSaving(true);
      }
      
      // 最低限のバリデーション
      if (!editState.title.trim()) {
        showSnackbar('サイト名を入力してください', 'error');
        if (!shouldSuppressSavingState) {
        setSaving(false);
        }
        return;
      }
      
      if (!editState.pages || editState.pages.length === 0) {
        showSnackbar('最低1ページの設定が必要です', 'error');
        if (!shouldSuppressSavingState) {
        setSaving(false);
        }
        return;
      }

      // カラーコードのバリデーション（無効な値がある場合はすべての更新をブロック）
      const bgColor = editState.settings?.backgroundColor;
      const pageBgColor = editState.settings?.pageBackgroundColor;
      
      // 背景色のチェック
      if (bgColor) {
        const trimmedBgColor = typeof bgColor === 'string' ? bgColor.trim() : '';
        if (trimmedBgColor !== '' && !isValidColorCode(trimmedBgColor)) {
          showSnackbar('背景色のカラーコードが無効です。有効な形式: #rrggbb または rgba(r, g, b, a)', 'error');
          if (!shouldSuppressSavingState) {
          setSaving(false);
          }
          return;
        }
      }

      // ページの背景色のチェック
      if (pageBgColor) {
        const trimmedPageBgColor = typeof pageBgColor === 'string' ? pageBgColor.trim() : '';
        if (trimmedPageBgColor !== '' && !isValidColorCode(trimmedPageBgColor)) {
          showSnackbar('ページの背景色のカラーコードが無効です。有効な形式: #rrggbb または rgba(r, g, b, a)', 'error');
          if (!shouldSuppressSavingState) {
          setSaving(false);
          }
          return;
        }
      }
      
        // settings はそのまま渡す。SEOタグ（metaTitle / metaDescription / metaKeywords）を settings にマージして保存
        const cleanSettings: any = {
          ...editState.settings,
          metaTitle: editState.metaTitle ?? editState.settings?.metaTitle,
          metaDescription: editState.metaDescription ?? editState.settings?.metaDescription,
          metaKeywords: editState.metaKeywords ?? editState.settings?.metaKeywords,
        };
        if (isCreating) {
          if (cleanSettings.ctaButton) {
            const cb = cleanSettings.ctaButton;
            cleanSettings.ctaButton = {
              ...cb,
              image: (cb.image && String(cb.image).startsWith('blob:')) ? '' : cb.image,
              hoverImage: (cb.hoverImage && String(cb.hoverImage).startsWith('blob:')) ? '' : cb.hoverImage,
            };
          }
          if (Array.isArray(cleanSettings.ctaButtons) && cleanSettings.ctaButtons.length > 0) {
            cleanSettings.ctaButtons = cleanSettings.ctaButtons.map((btn: any) => ({
              ...btn,
              image: (btn.image && String(btn.image).startsWith('blob:')) ? '' : btn.image,
              hoverImage: (btn.hoverImage && String(btn.hoverImage).startsWith('blob:')) ? '' : btn.hoverImage,
            }));
          }
          if (cleanSettings.logo && String(cleanSettings.logo).startsWith('blob:')) cleanSettings.logo = '';
          if (cleanSettings.favicon && String(cleanSettings.favicon).startsWith('blob:')) cleanSettings.favicon = '';
        }

        const pagesForSave = (editState.pages || []).map((p: any) => {
          const sanitized = {
            ...p,
            backgroundImage: (p.backgroundImage && String(p.backgroundImage).startsWith('blob:')) ? '' : p.backgroundImage,
          };
          if (isCreating && Array.isArray(p.horizontalSlides) && p.horizontalSlides.length > 0) {
            sanitized.horizontalSlides = p.horizontalSlides.map((s: any) => ({
              ...s,
              backgroundImage: (s.backgroundImage && String(s.backgroundImage).startsWith('blob:')) ? '' : s.backgroundImage,
            }));
          }
          return sanitized;
        });
        const siteData = {
          title: editState.title.trim(),
          description: editState.description || '',
          status: editState.status,
          pages: pagesForSave,
          maxRows: editState.maxRows || 1,
          maxColumns: editState.maxColumns || 1,
          customDomain: editState.customDomain,
          companyUserId: editState.companyUserId,
          sslEnabled: editState.sslEnabled,
          ...(cleanSettings != null && { settings: cleanSettings }),
        };
      
      if (isCreating) {
        isSavingFromCreateRef.current = true;
        const newSiteId = await createSite(siteData);
        
        const slotIdToUrl = new Map<string, string>();
        const pendingPages = pendingPageImageFilesRef.current;
        if (pendingPages.size > 0) {
          await Promise.all(
            Array.from(pendingPages.entries()).map(async ([slotId, file]) => {
              try {
                const url = await uploadImage(file, newSiteId, slotId, { useUniqueName: true });
                slotIdToUrl.set(slotId, url);
              } catch (err) {
                console.error(`ページ画像アップロード失敗 (${slotId}):`, err);
              }
            })
          );
          pendingPageImageFilesRef.current.clear();
        }
        
        let backgroundUrl: string | null = null;
        const pendingBg = pendingBackgroundImageFileRef.current;
        if (pendingBg) {
          try {
            backgroundUrl = await uploadImage(pendingBg, newSiteId, 'background', { useUniqueName: true });
          } catch (err) {
            console.error('背景画像アップロード失敗:', err);
          }
          pendingBackgroundImageFileRef.current = null;
        }
        
        const updatedPages = pagesForSave.map((p: any) => {
          const sid = `${p.row}-${p.column}`;
          const url = slotIdToUrl.get(sid);
          return url ? { ...p, backgroundImage: url } : p;
        });
        const ctaImageUrlMap = new Map<string, string>();
        const pendingCtaImages = pendingCtaImageFilesRef.current;
        if (pendingCtaImages.size > 0) {
          await Promise.all(
            Array.from(pendingCtaImages.entries()).map(async ([buttonId, file]) => {
              try {
                const url = await uploadImage(file, newSiteId, `cta-image-${buttonId}`, { useUniqueName: true });
                ctaImageUrlMap.set(buttonId, url);
              } catch (err) {
                console.error(`CTA画像アップロード失敗 (${buttonId}):`, err);
              }
            })
          );
          pendingCtaImageFilesRef.current.clear();
        }
        const ctaHoverUrlMap = new Map<string, string>();
        const pendingCtaHoverImages = pendingCtaHoverImageFilesRef.current;
        if (pendingCtaHoverImages.size > 0) {
          await Promise.all(
            Array.from(pendingCtaHoverImages.entries()).map(async ([buttonId, file]) => {
              try {
                const url = await uploadImage(file, newSiteId, `cta-hover-${buttonId}`, { useUniqueName: true });
                ctaHoverUrlMap.set(buttonId, url);
              } catch (err) {
                console.error(`CTAホバー画像アップロード失敗 (${buttonId}):`, err);
              }
            })
          );
          pendingCtaHoverImageFilesRef.current.clear();
        }

        let logoUrl: string | null = null;
        let faviconUrl: string | null = null;
        if (pendingLogoFileRef.current) {
          try {
            logoUrl = await uploadImage(pendingLogoFileRef.current, newSiteId, 'logo', { useUniqueName: true });
          } catch (err) {
            console.error('ロゴアップロード失敗:', err);
          }
          pendingLogoFileRef.current = null;
        }
        if (pendingFaviconFileRef.current) {
          try {
            faviconUrl = await uploadImage(pendingFaviconFileRef.current, newSiteId, 'favicon', { useUniqueName: true });
          } catch (err) {
            console.error('ファビコンアップロード失敗:', err);
          }
          pendingFaviconFileRef.current = null;
        }

        const slideKeyToUrl = new Map<string, string>();
        const pendingSlides = pendingSlideImageFilesRef.current;
        if (pendingSlides.size > 0) {
          await Promise.all(
            Array.from(pendingSlides.entries()).map(async ([key, file]) => {
              try {
                const url = await uploadImage(file, newSiteId, key, { useUniqueName: true });
                slideKeyToUrl.set(key, url);
              } catch (err) {
                console.error(`横スライド画像アップロード失敗 (${key}):`, err);
              }
            })
          );
          pendingSlideImageFilesRef.current.clear();
        }

        let pagesToSave = updatedPages;
        if (slideKeyToUrl.size > 0) {
          pagesToSave = updatedPages.map((p: any) => {
            const slides = p.horizontalSlides;
            if (!Array.isArray(slides) || slides.length === 0) return p;
            return {
              ...p,
              horizontalSlides: slides.map((s: any, i: number) => {
                const key = `${p.id}-slide-${i + 1}`;
                const url = slideKeyToUrl.get(key);
                return url ? { ...s, backgroundImage: url } : s;
              }),
            };
          });
        }

        const hasImageUpdates = slotIdToUrl.size > 0 || backgroundUrl || ctaImageUrlMap.size > 0 || ctaHoverUrlMap.size > 0 || logoUrl != null || faviconUrl != null || slideKeyToUrl.size > 0;
        let finalSettings = editState.settings;
        if (hasImageUpdates) {
          const updates: any = { pages: pagesToSave };
          const nextSettings = { ...editState.settings };
          if (backgroundUrl) nextSettings.backgroundImage = backgroundUrl;
          if (logoUrl != null) nextSettings.logo = logoUrl;
          if (faviconUrl != null) nextSettings.favicon = faviconUrl;
          if (ctaImageUrlMap.size > 0 || ctaHoverUrlMap.size > 0) {
            if (Array.isArray(nextSettings.ctaButtons) && nextSettings.ctaButtons.length > 0) {
              nextSettings.ctaButtons = nextSettings.ctaButtons.map((btn: any) => ({
                ...btn,
                ...(ctaImageUrlMap.has(btn.id) && { image: ctaImageUrlMap.get(btn.id) }),
                ...(ctaHoverUrlMap.has(btn.id) && { hoverImage: ctaHoverUrlMap.get(btn.id) }),
              }));
            }
            if (nextSettings.ctaButton && Array.isArray(nextSettings.ctaButtons) && nextSettings.ctaButtons.length > 0) {
              const firstButton = nextSettings.ctaButtons[0];
              nextSettings.ctaButton = {
                ...nextSettings.ctaButton,
                image: firstButton.image,
                hoverImage: firstButton.hoverImage,
              };
            }
          }
          updates.settings = nextSettings;
          finalSettings = nextSettings;
          await updateSite(newSiteId, updates);
        }
        
        if (editState.status === 'published') {
          await publishSite(newSiteId, editState.customDomain);
        }
        
        const initialEditState: SiteEditState = { ...editState, pages: pagesToSave, settings: finalSettings };
        try {
          sessionStorage.setItem(`${SESSION_KEY_CREATED_EDIT_STATE}_${newSiteId}`, JSON.stringify(initialEditState));
        } catch (_) {}
        pendingCreatedStateRef.current = { siteId: newSiteId, editState: initialEditState };
        navigate(`/admin/sites/${newSiteId}/edit`, { replace: true, state: { newSiteId, initialEditState } });
        if (!shouldSuppress) {
          showSnackbar('サイトが正常に作成されました', 'success');
        }
      } else if (siteId && siteId !== 'create') {
        // 既存サイトの更新
        await updateSite(siteId, siteData);
        
        // 公開設定がある場合は公開
        if (editState.status === 'published') {
          await publishSite(siteId, editState.customDomain);
        } else if (editState.status === 'unpublished') {
          await unpublishSite(siteId);
        }
        
        // 更新後は編集画面に留まる（一覧には戻らない）
        if (!shouldSuppress) {
          showSnackbar('サイトが正常に更新されました', 'success');
        }
      } else {
        console.error('無効なサイトIDです:', siteId);
        showSnackbar('サイトIDが無効です', 'error');
        return;
      }
      
      // 保存完了後、ローカルストレージをクリア
      const storageKey = `siteEditState_${siteId}`;
      localStorage.removeItem(storageKey);
      
    } catch (error) {
      console.error('保存に失敗しました:', error);
      const errorMessage = error instanceof Error ? error.message : '保存に失敗しました';
      showSnackbar(errorMessage, 'error');
    } finally {
      isSavingFromCreateRef.current = false;
      if (!shouldSuppressSavingState) {
      setSaving(false);
      }
    }
  };

  // PopupCTATab用のコールバック関数
  const handleUpdateCtaButton = useCallback((ctaButtonData: { html: string; css: string; image?: string; hoverImage?: string; enabled: boolean; bottomPercent?: number; widthPercent?: number }) => {
    setEditState(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        ctaButton: ctaButtonData
      }
    }));
  }, []);

  const handleUpdateCtaButtons = useCallback((ctaButtons: any) => {
    setEditState(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        ctaButtons: ctaButtons
      }
    }));
  }, []);

  const renderContent = () => {
    switch (activeMenu) {
      case 'settings':
        return (
          <SiteSettingsTab 
            editState={editState} 
            updateEditState={updateEditState}
            siteId={siteId}
            cachedImages={imageLibraryCache}
            preloaded={imageLibraryLoaded}
            onSave={handleSave}
            onPendingFaviconFile={(file) => { pendingFaviconFileRef.current = file; }}
          />
        );
      case 'site':
        return (
          <SiteSettingsTab 
            editState={editState} 
            updateEditState={updateEditState}
            siteId={siteId}
            cachedImages={imageLibraryCache}
            preloaded={imageLibraryLoaded}
            onSave={handleSave}
            onPendingFaviconFile={(file) => { pendingFaviconFileRef.current = file; }}
          />
        );
      case 'page':
        return (
          <PageConfigurationTab 
            editState={editState} 
            updateEditState={updateEditState}
            siteId={siteId || undefined}
            cachedImages={imageLibraryCache}
            preloaded={imageLibraryLoaded}
            onPendingPageImageFile={(slotId, file) => {
              if (file) pendingPageImageFilesRef.current.set(slotId, file);
              else pendingPageImageFilesRef.current.delete(slotId);
            }}
            onPendingBackgroundImageFile={(file) => {
              pendingBackgroundImageFileRef.current = file;
            }}
            onPendingSlideImageFile={(slideKey, file) => {
              if (file) pendingSlideImageFilesRef.current.set(slideKey, file);
              else pendingSlideImageFilesRef.current.delete(slideKey);
            }}
          />
        );
      case 'cta':
        return (
          <PopupCTATab 
            editState={editState} 
            siteId={siteId || undefined} 
            onUpdateCtaButton={handleUpdateCtaButton}
            onUpdateCtaButtons={handleUpdateCtaButtons}
            cachedImages={imageLibraryCache}
            preloaded={imageLibraryLoaded}
            onPendingCtaImageFile={(buttonId, file) => {
              if (file) pendingCtaImageFilesRef.current.set(buttonId, file);
              else pendingCtaImageFilesRef.current.delete(buttonId);
            }}
            onPendingCtaHoverImageFile={(buttonId, file) => {
              if (file) pendingCtaHoverImageFilesRef.current.set(buttonId, file);
              else pendingCtaHoverImageFilesRef.current.delete(buttonId);
            }}
          />
        );
      case 'seo':
        return <SEOTagsTab editState={editState} updateEditState={updateEditState} />;
      case 'company':
        return <CompanyInfoTab editState={editState} updateEditState={updateEditState} />;
      case 'library':
        return siteId && siteId !== 'create' ? (
          <ImageLibraryTab 
            siteId={siteId}
            editState={editState}
            updateEditState={updateEditState}
            cachedImages={imageLibraryCache}
            onImagesChange={setImageLibraryCache}
            preloaded={imageLibraryLoaded}
          />
        ) : null;
      default:
        return null;
    }
  };

  // メインメニュー項目
  const MENU_ITEMS = [
    { label: '設定', icon: <SettingsIcon />, id: 'settings' },
    { label: 'サイト', icon: null, id: 'site' },
    { label: 'ページ', icon: null, id: 'page' },
    { label: 'CTA', icon: null, id: 'cta' },
    { label: 'SEO / タグ', icon: null, id: 'seo' },
    { label: '会社情報', icon: null, id: 'company' },
    { label: 'ライブラリ', icon: null, id: 'library' },
  ];

  const pageSubMenuWidth = 176; // ページ設定メニューの幅（メインメニュー開時240px - 閉時64px = 176px）

  if (!editInitialized && !isCreating && siteId) {
    // 既存サイト読み込み中は何も表示しない（トグルが初期値→実際の値に「動く」のを防ぐ）
    return null;
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* ページ設定メニュー */}
      <Box
        sx={{
          width: pageSubMenuWidth,
          flexShrink: 0,
          backgroundColor: '#F0F0F0',
          borderLeft: '1px solid #E0E0E0',
        }}
      >
        <List sx={{ p: 0, m: 0, pt: 1 }}>
          {MENU_ITEMS.map((item) => {
            const isSelected = activeMenu === item.id;
            const isTitle = item.id === 'settings';
            return (
              <ListItem key={item.id} disablePadding>
                {isTitle ? (
                  <Box
                    sx={{
                      py: 2,
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {item.icon && (
                      <Box sx={{
                        width: '64px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                        <ListItemIcon sx={{ 
                          color: '#666666',
                          minWidth: 'auto',
                          margin: 0,
                          '& svg': { fontSize: '1.5rem' },
                        }}>
                          {item.icon}
                        </ListItemIcon>
                      </Box>
                    )}
                    {!item.icon && (
                      <Box sx={{ width: '64px' }} />
                    )}
                    <Typography
                      sx={{
                        color: '#666666',
                        fontWeight: 600,
                        fontSize: '16px',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ) : (
                  <ListItemButton
                    onClick={() => handleMenuClick(item.id)}
                    selected={isSelected}
                    sx={{
                      position: 'relative',
                      py: 1,
                      px: 2,
                      minHeight: '40px',
                      backgroundColor: 'transparent !important',
                      '&.Mui-selected': {
                        backgroundColor: '#F7F7F7 !important',
                        '&:hover': {
                          backgroundColor: '#F7F7F7 !important',
                        },
                      },
                      '&:not(.Mui-selected):hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: isSelected ? '#1B76D2' : 'transparent',
                      },
                      '& .MuiListItemText-primary': {
                        color: isSelected ? '#1B76D2' : '#000000',
                        fontWeight: isSelected ? 500 : 400,
                        fontSize: '16px',
                      },
                    }}
                  >
                    {!item.icon && (
                      <Box sx={{ width: '24px', marginRight: 2 }} />
                    )}
                    <ListItemText 
                      primary={item.label}
                      sx={{
                        marginLeft: 0,
                        whiteSpace: 'nowrap',
                      }}
                    />
                  </ListItemButton>
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* コンテンツ表示エリア */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* ヘッダー */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            px: 3,
            pt: 3,
            pb: 2,
            backgroundColor: '#F5F5F5'
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton 
              onClick={handleBack} 
              sx={{ 
                backgroundColor: '#FFFFFF',
                borderRadius: '50%',
                width: 40,
                height: 40,
                color: '#000000',
                padding: 0,
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />
            </IconButton>
            <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#000000' }}>
              {isCreating ? '新規作成' : '編集'}
            </Typography>
            {!isCreating && (
              <>
                {editState.title && (
                  <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 600 }}>
                    : {editState.title}
                  </Typography>
                )}
                {(() => {
                  const site = sites.find(s => s.id === siteId);
                  if (site) {
                    const actualStatus = SiteService.getActualStatusSync(site, domainConfig);
                    const statusLabel = getStatusLabel(actualStatus);
                    const statusColor = getStatusColor(actualStatus);
                    const publishedChip = isStatusPublished(actualStatus);
                    return (
                      <Chip
                        label={statusLabel}
                        sx={{
                          backgroundColor: statusColor,
                          color: publishedChip ? '#FFFFFF' : 'rgba(0, 0, 0, 0.6)',
                          fontSize: '12px',
                          fontWeight: 500,
                          height: '24px',
                          borderRadius: '6px',
                          ml: 1,
                        }}
                      />
                    );
                  }
                  return null;
                })()}
                {siteUrl && (
                  <Link 
                    href={siteUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ 
                      fontSize: '14px',
                      color: '#1B76D2',
                      textDecoration: 'none',
                      ml: 1,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {siteUrl}
                  </Link>
                )}
              </>
            )}
          </Box>
          
          <Box display="flex" gap={1.5} alignItems="center">
            <IconButton
              onClick={handlePreview}
              sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                width: 40,
                height: 40,
                border: '1px solid #E0E0E0',
                padding: 0,
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <PreviewIcon sx={{ fontSize: '20px', color: '#666666' }} />
            </IconButton>
            <Button
              startIcon={
                <Box
                  sx={{ 
                    position: 'relative',
                    padding: '2px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '14px',
                    display: 'inline-flex',
                  }}
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      const newStatus = editState.status === 'published' ? 'unpublished' : 'published';
                      updateEditState({ status: newStatus });
                    }}
                    sx={{
                      position: 'relative',
                      width: '48px',
                      height: '24px',
                      backgroundColor: editState.status === 'published' ? '#1B76D2' : '#9E9E9E',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '50%',
                        transform: editState.status === 'published' ? 'translateX(24px)' : 'translateX(0px)',
                        transition: 'transform 0.3s',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      }} 
                    />
                  </Box>
                </Box>
              }
              onClick={() => {
                const newStatus = editState.status === 'published' ? 'unpublished' : 'published';
                updateEditState({ status: newStatus });
              }}
              sx={{
                backgroundColor: editState.status === 'published' ? '#1B76D2' : '#E7E7E7',
                color: editState.status === 'published' ? '#FFFFFF' : '#000000',
                borderRadius: '8px',
                textTransform: 'none',
                padding: '8px 16px',
                minWidth: '120px',
                width: '120px',
                height: '40px',
                border: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: editState.status === 'published' ? '#1565C0' : '#D7D7D7',
                },
                '& .MuiButton-startIcon': {
                  marginRight: 0.5,
                },
              }}
            >
              {editState.status === 'published' ? '公開' : '非公開'}
            </Button>
            <SaveButton
              onClick={handleSaveClick}
              saving={saving}
              sx={{
                padding: '8px 16px',
                height: '40px',
              }}
            />
          </Box>
        </Box>

        {/* コンテンツエリア */}
        <Box sx={{ flex: 1, overflow: 'auto', backgroundColor: '#F5F5F5', p: 0, m: 0 }}>
          {renderContent()}
        </Box>
      </Box>

      <ConfirmDialog
        open={saveConfirmOpen}
        onClose={handleSaveCancel}
        onConfirm={handleSaveConfirm}
        message={
          <>
            編集中の内容で保存 (上書き) します。
            <br />
            よろしいですか?
          </>
        }
        confirmText="保存"
        confirmColor="#FF9800"
        confirmHoverColor="#F57C00"
        confirmDisabled={modalSaving}
        reverseButtons={true}
      />

      {/* 保存成功メッセージモーダル */}
      {saveSuccessOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1400,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px 32px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              minWidth: '300px',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '16px', color: '#000000', fontWeight: 500 }}>
              保存しました
            </Typography>
          </Box>
        </Box>
      )}

      {/* スナックバー - 右からスライドイン */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

